import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';

/**
 * Background component: asks the browser for the user's location once
 * enabled and within the configured time window, and POSTs it to the backend
 * every `interval_minutes`. Mount once in the authenticated shell.
 *
 * Renders nothing. All behaviour is driven by /api/settings/location-ping.
 */
export default function LocationPinger() {
  const { user } = useAuth();
  const intervalRef = useRef(null);
  const settingsRef = useRef(null);

  const withinWindow = useCallback((settings) => {
    if (!settings?.window_start || !settings?.window_end) return true;
    const now = new Date();
    const [sh, sm] = settings.window_start.split(':').map(Number);
    const [eh, em] = settings.window_end.split(':').map(Number);
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    // Simple non-overnight window
    return nowMin >= startMin && nowMin <= endMin;
  }, []);

  const sendPing = useCallback((force = false) => {
    const s = settingsRef.current;
    if (!force) {
      if (!s || !s.enabled) return;
      if (!Array.isArray(s.target_roles) || !s.target_roles.includes(user?.role)) return;
      if (!withinWindow(s)) return;
    }
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        api
          .post('/location-pings', {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            source: force ? 'admin-request' : 'browser',
          })
          .catch(() => { /* silent */ });
      },
      () => { /* silent */ },
      { enableHighAccuracy: force, maximumAge: force ? 0 : 60_000, timeout: 15_000 }
    );
  }, [user?.role, withinWindow]);

  // Silent on-demand polling for admin-requested pings (no notification to agent)
  useEffect(() => {
    if (!user || !['agent', 'admin'].includes(user.role)) return;
    let cancelled = false;
    const checkRequested = async () => {
      try {
        const res = await api.get('/location-pings/should-ping');
        if (cancelled) return;
        if (res.data?.requested) {
          sendPing(true);  // force-ping outside window/disabled — admin asked for it
        }
      } catch { /* silent */ }
    };
    // poll every 60s
    const id = setInterval(checkRequested, 60_000);
    checkRequested();
    return () => { cancelled = true; clearInterval(id); };
  }, [user, sendPing]);

  useEffect(() => {
    if (!user || !['agent', 'admin'].includes(user.role)) return;

    let cancelled = false;

    const loadAndSchedule = async () => {
      try {
        const res = await api.get('/settings/location-ping');
        if (cancelled) return;
        settingsRef.current = res.data;

        // Clear any existing timer
        if (intervalRef.current) clearInterval(intervalRef.current);

        if (!res.data?.enabled) return;

        // Prime one immediate ping (will no-op if outside window)
        sendPing();

        const ms = Math.max(5, res.data.interval_minutes || 30) * 60 * 1000;
        intervalRef.current = setInterval(sendPing, ms);
      } catch (err) {
        // silent
      }
    };

    loadAndSchedule();
    // Re-read settings every 10 minutes so admin toggles propagate reasonably fast
    const reloadId = setInterval(loadAndSchedule, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearInterval(reloadId);
    };
  }, [user, sendPing]);

  return null;
}
